import api from '../../services/api';

export const pulseAPI = {
  devices: () => api.get('/api/pulse/devices'),
  discovery: () => api.get('/api/pulse/discovery'),
  detail: id => api.get(`/api/pulse/assets/${id}`),
  loginDays: (id, params, signal) => api.get(`/api/pulse/assets/${id}/login-days`, { params, signal }),
  management: id => api.get(`/api/pulse/assets/${id}/management`),
  requestRemote: id => api.post(`/api/pulse/assets/${id}/remote/request`),
  verifyRemote: (id, sessionId, code) => api.post(`/api/pulse/assets/${id}/remote/${sessionId}/verify`, { code }),
  launchRemote: (id, sessionId) => api.post(`/api/pulse/assets/${id}/remote/${sessionId}/launch`),
  endRemote: (id, sessionId) => api.post(`/api/pulse/assets/${id}/remote/${sessionId}/end`),
  remoteSettings: (id, captureMode) => api.patch(`/api/pulse/assets/${id}/remote-settings`, { captureMode }),
  remoteSession: sessionId => api.get(`/api/pulse/remote/sessions/${sessionId}`),
  remoteMetrics: (sessionId, metrics) => api.post(`/api/pulse/remote/sessions/${sessionId}/metrics`, metrics),
  remoteTicket: (sessionId, signal) => api.post(`/api/pulse/remote/sessions/${sessionId}/ticket`, undefined, { signal }),
  releases: () => api.get('/api/pulse/releases'),
  publishRelease: (manifest, file, onUploadProgress) => { const form = new FormData(); form.append('manifest', JSON.stringify(manifest)); form.append('package', file); return api.post('/api/pulse/releases', form, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress }); },
  assignUpdate: (id, releaseId) => api.post(`/api/pulse/assets/${id}/updates`, { releaseId }),
  collectDiagnostics: id => api.post(`/api/pulse/assets/${id}/diagnostics`),
  saveDiagnostics: async (jobId, part = 0, sha256) => {
    const response = await api.get(`/api/pulse/diagnostics/${jobId}/download`, { responseType: 'blob', params: { part } });
    if (sha256 && window.crypto?.subtle) {
      const digest = await window.crypto.subtle.digest('SHA-256', await response.data.arrayBuffer());
      const actual = Array.from(new Uint8Array(digest), value => value.toString(16).padStart(2, '0')).join('');
      if (actual !== sha256) throw new Error('Diagnostic download checksum mismatch. Download this part again.');
    }
    const url = URL.createObjectURL(response.data), link = document.createElement('a');
    link.href = url; link.download = `Pulse-diagnostics-${jobId}-part-${part + 1}.zip`; document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  },
  pair: (id, code, deviceId) => api.post(`/api/pulse/assets/${id}/pair`, { code, deviceId }),
  command: (id, kind, parameters = {}, confirmed = false) => api.post(`/api/pulse/assets/${id}/commands`, { kind, parameters, confirmed }),
  folder: (id, path, offset = 0) => api.get(`/api/pulse/assets/${id}/folders`, { params: { path, offset } }),
  result: id => api.get(`/api/pulse/commands/${id}`),
  history: (id, status = 'all', cursor = '') => api.get(`/api/pulse/assets/${id}/commands`, { params: { status, cursor } }),
  wallpapers: id => api.get(`/api/pulse/assets/${id}/wallpapers`),
  wallpaperImage: (id, wallpaperId) => api.get(`/api/pulse/assets/${id}/wallpapers/${wallpaperId}/image`, { responseType: 'blob' }),
  uploadWallpaper: (id, file, crop) => { const form = new FormData(); form.append('file', file); form.append('crop', JSON.stringify(crop)); return api.post(`/api/pulse/assets/${id}/wallpapers`, form, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  removeWallpaper: (id, wallpaperId) => api.delete(`/api/pulse/assets/${id}/wallpapers/${wallpaperId}`),
  applyWallpaper: (id, wallpaperId, sid) => api.post(`/api/pulse/assets/${id}/wallpapers/${wallpaperId}/apply`, { sid }),
  usbDevicePolicy: (id, instanceId, mode) => api.post(`/api/pulse/assets/${id}/usb-policy`, { instanceId, mode }),
  transfers: id => api.get(`/api/pulse/assets/${id}/transfers`),
  createTransfer: (id, path, directory) => api.post(`/api/pulse/assets/${id}/transfers`, { path, directory }),
  cancelTransfer: (id, transferId) => api.delete(`/api/pulse/assets/${id}/transfers/${transferId}`),
  saveTransfer: async (id, transfer) => {
    const response = await api.post(`/api/pulse/assets/${id}/transfers/${transfer.transferId}/ticket`);
    const url = new URL(response.data.url, process.env.REACT_APP_BACKEND_URL || window.location.origin);
    const link = document.createElement('a'); link.href = url.href; link.download = transfer.name; document.body.appendChild(link); link.click(); link.remove();
  },
  revoke: id => api.delete(`/api/pulse/assets/${id}/connection`),
  download: async () => {
    const response = await api.get('/api/pulse/installer', { responseType: 'blob' });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a'); link.href = url; link.download = 'AssetFlowPulseConnectSetup.exe'; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  },
};

export const pulseError = error => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (typeof detail?.message === 'string') return detail.message;
  if (Array.isArray(detail)) return detail.map(item => item.msg || 'Invalid request').join('; ');
  return error?.message || 'Pulse request failed';
};
